import { useState } from "react";
import { Plus, Trash2, Pencil, History } from "lucide-react";
import { fmtTL, fmtNum } from "../../lib/format";
import CorrectionLineForm from "./CorrectionLineForm";

const HEADERS = ["Hizmet", "Miktar", "Birim", "Birim Fiyat", "Toplam"];
const thAlign = (i) => (i === 0 ? "text-left" : i === 2 ? "text-center" : "text-right");
const SEP = " — ";
const CORR = " (düzeltme)";
// "ZİRAAT 1.BÖLGE — YEMEK" → [bölge, kalem]; ayraç yoksa bölgesiz
const splitRegion = (d) => { const i = (d || "").indexOf(SEP); return i > 0 ? [d.slice(0, i), d.slice(i + SEP.length)] : ["", d || ""]; };
const iconBtn = (color) => `opacity-0 group-hover:opacity-100 focus:opacity-100 inline-flex items-center justify-center w-4 h-4 rounded border bg-white transition-all ${color}`;

function groupByRegion(items) {
  const groups = [];
  items.forEach((it, i) => {
    const [region, name] = splitRegion(it.description);
    let g = groups.find((x) => x.region === region);
    if (!g) groups.push((g = { region, rows: [], total: 0 }));
    const isCorr = (it.description || "").endsWith(CORR);
    g.rows.push({ ...it, name, i, isCorr, corrName: isCorr ? name.slice(0, -CORR.length) : name });
    g.total += Number(it.total) || 0;
  });
  return groups;
}

// Excel ÖZET sayfası gibi: bölge öneki atılır, aynı alt hizmet tüm bölgelerde toplanır (miktar + tutar; birim fiyat = tutar ÷ miktar), tutara göre sıralı
function aggregateByService(items) {
  const map = new Map();
  items.forEach((it, i) => {
    const [, name] = splitRegion(it.description);
    let a = map.get(name);
    if (!a) {
      const isCorr = (it.description || "").endsWith(CORR);
      map.set(name, (a = { ...it, name, i, isCorr, corrName: isCorr ? name.slice(0, -CORR.length) : name, quantity: 0, total: 0, regions: 0 }));
    }
    a.quantity += Number(it.quantity) || 0;
    a.total += Number(it.total) || 0;
    a.regions += 1;
  });
  return [...map.values()].map((a) => ({ ...a, unit_price: a.quantity ? a.total / a.quantity : a.unit_price })).sort((x, y) => y.total - x.total);
}

function ItemRow({ it, name, testid, onAddBelow, onDelete, onEdit }) {
  const neg = Number(it.total) < 0;
  const shown = it.isCorr ? name.slice(0, -CORR.length) : name;
  return (
    <tr className={`border-t border-slate-100 group ${it.isCorr ? "bg-violet-50/40" : ""}`} data-testid={testid} data-correction={it.isCorr ? "1" : undefined}>
      <td className="px-3 py-1.5 text-xs whitespace-normal break-words">
        <span className={`inline-flex items-center gap-1.5 ${it.isCorr ? "text-violet-900 font-medium" : ""}`}>
          {shown}
          {it.regions > 1 && <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[9px] font-semibold" title="Bu alt hizmet birden çok bölgede var; miktar ve tutar toplandı" data-testid={`${testid}-regions`}>{it.regions} bölge</span>}
          {it.isCorr && <span className="px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 text-[9px] font-bold uppercase" title="Düzeltme farkları şubesine girilen ± satır">Düzeltme</span>}
          {onAddBelow && (
            <button type="button" onClick={() => onAddBelow(it)} data-testid={`${testid}-add-below`} title="Bu satırın altına ± düzeltme satırı ekle (bölge: bu satırın bölgesi)" className={iconBtn("border-violet-300 text-violet-700 hover:bg-violet-600 hover:text-white")}>
              <Plus className="w-3 h-3" />
            </button>
          )}
          {it.isCorr && onEdit && (
            <button type="button" onClick={() => onEdit(it)} data-testid={`${testid}-edit`} title="Bu düzeltme satırını yerinde düzenle (ad, miktar, tutar)" className={iconBtn("border-sky-300 text-sky-700 hover:bg-sky-600 hover:text-white")}>
              <Pencil className="w-3 h-3" />
            </button>
          )}
          {it.isCorr && onDelete && (
            <button type="button" onClick={() => onDelete(it)} data-testid={`${testid}-delete`} title="Bu düzeltme satırını kaldır (DÜZELTME FARKLARI şubesindeki kayıt silinir)" className={iconBtn("border-red-300 text-red-700 hover:bg-red-600 hover:text-white")}>
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </span>
      </td>
      <td className="numeric-cell px-3 py-1.5 text-xs">{fmtNum(it.quantity)}</td>
      <td className="px-3 py-1.5 text-xs text-center">{it.unit}</td>
      <td className={`numeric-cell px-3 py-1.5 text-xs ${neg ? "text-red-700" : ""}`}>{fmtTL(it.unit_price)}</td>
      <td className={`numeric-cell px-3 py-1.5 text-xs font-semibold ${neg ? "text-red-700" : ""}`}>{fmtTL(it.total)}</td>
    </tr>
  );
}

// Hakediş fatura kalemleri. Varsayılan görünüm "Özet": Excel ÖZET sayfası gibi her alt hizmet bir kez (bölgeler toplanır). "Bölge Detayı": bölge bazında (BÖLGE başlığı → hizmet, YEMEK, KAHVALTI, YOL, düzeltmeler → bölge toplamı); bölgesiz kalemler (ATM ceza vb.) en sonda.
// Sabit kolon düzeni: açıklama satır kırar, miktar / birim / birim fiyat / toplam her zaman görünür.
// editable: bölge detayında her satırın altına ± düzeltme satırı eklenebilir; düzeltme satırı yerinde düzenlenir / kaldırılır (bölgenin DÜZELTME FARKLARI şubesi)
export default function InvoiceItems({ items, testid = "invoice-items", editable = false, regions = [], onSaveCorrection, onEditCorrection, onDeleteCorrection, onHistory, saving = false }) {
  const [adding, setAdding] = useState(null);   // { afterIndex, regionName } | { afterIndex: -1 }
  const [editing, setEditing] = useState(null); // düzenlenen satırın index'i
  const [view, setView] = useState("summary");  // summary | regions
  const groups = groupByRegion(items);
  const regional = groups.some((g) => g.region);
  const summary = view === "summary" && regional;
  const regionIdOf = (name) => regions.find((r) => r.name === name)?.id;
  const canEdit = editable && regions.length > 0 && !!onSaveCorrection;
  const lineOf = (g, it) => ({ entry_id: it.entry_id, region: g.region, name: it.corrName, total: it.total });
  const form = (key, regionName) => (
    <CorrectionLineForm key={key} regions={regions} defaultRegionId={regionIdOf(regionName)} saving={saving} testid={`${testid}-corr-form`}
      onSave={async (p) => { const ok = await onSaveCorrection(p); if (ok !== false) setAdding(null); }} onCancel={() => setAdding(null)} />
  );
  const editForm = (g, it) => (
    <CorrectionLineForm key={`edit-${it.i}`} regions={regions} saving={saving} testid={`${testid}-corr-edit`}
      initial={{ name: it.corrName, region_id: regionIdOf(g.region), quantity: it.quantity, unit: it.unit, unit_price: it.unit_price, total: it.total }}
      onSave={async (p) => { const ok = await onEditCorrection(lineOf(g, it), p); if (ok !== false) setEditing(null); }} onCancel={() => setEditing(null)} />
  );
  const switchView = (v) => { setView(v); setAdding(null); setEditing(null); };
  return (
    <table className="w-full tbl table-fixed" data-testid={testid}>
      <colgroup>
        <col />
        <col style={{ width: 90 }} />
        <col style={{ width: 70 }} />
        <col style={{ width: 130 }} />
        <col style={{ width: 140 }} />
      </colgroup>
      <thead>
        {regional && (
          <tr className="bg-slate-50">
            <td colSpan={5} className="px-3 py-1">
              <div className="flex items-center gap-1" role="tablist" data-testid={`${testid}-view`}>
                <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mr-1">Görünüm</span>
                {[["summary", "Özet (alt hizmet bazlı)"], ["regions", "Bölge Detayı"]].map(([v, label]) => (
                  <button key={v} type="button" role="tab" aria-selected={view === v} onClick={() => switchView(v)} data-testid={`${testid}-view-${v}`}
                    className={`px-2 py-0.5 rounded text-[10px] font-semibold border transition-colors ${view === v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"}`}>{label}</button>
                ))}
                {summary && <span className="ml-2 text-[10px] text-slate-500">Her alt hizmet bir kez; birden çok bölgedeki miktar ve tutar toplanır. Düzeltme eklemek için Bölge Detayı'na geçin.</span>}
              </div>
            </td>
          </tr>
        )}
        <tr>{HEADERS.map((h, i) => <th key={h} className={`px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold ${thAlign(i)}`}>{h}</th>)}</tr>
      </thead>
      <tbody>
        {summary ? <SummaryRows rows={aggregateByService(items)} testid={testid} /> : groups.map((g) => (
          <RegionBlock key={g.region || "_"} g={g} regional={regional} testid={testid} adding={adding} form={form} editing={editing} editForm={canEdit && onEditCorrection ? editForm : null}
            onAddBelow={canEdit ? (it) => { setEditing(null); setAdding({ afterIndex: it.i, regionName: g.region }); } : undefined}
            onEdit={canEdit && onEditCorrection ? (it) => { setAdding(null); setEditing(it.i); } : undefined}
            onDelete={canEdit && onDeleteCorrection ? (it) => onDeleteCorrection(lineOf(g, it)) : undefined} />
        ))}
        {items.length === 0 && <tr><td colSpan={5} className="px-3 py-2 text-xs text-slate-400">Kalem yok</td></tr>}
        {canEdit && adding?.afterIndex === -1 && form("corr-end", null)}
        {(canEdit || onHistory) && (
          <tr className="border-t border-slate-200">
            <td colSpan={5} className="px-3 py-1.5">
              <div className="flex flex-wrap items-center gap-2">
                {canEdit && (
                  <button type="button" onClick={() => { setEditing(null); setAdding({ afterIndex: -1 }); }} data-testid={`${testid}-add-correction`}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-violet-300 bg-violet-50 text-violet-800 text-[10px] font-bold uppercase hover:bg-violet-600 hover:text-white hover:border-violet-600 transition-colors">
                    <Plus className="w-3 h-3" /> Düzeltme Satırı Ekle (± fark)
                  </button>
                )}
                {onHistory && (
                  <button type="button" onClick={onHistory} data-testid={`${testid}-corr-history`} title="Bölgenin DÜZELTME FARKLARI şubesine dönem dönem girilen tüm ± kayıtlar"
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-slate-300 bg-white text-slate-700 text-[10px] font-bold uppercase hover:bg-slate-800 hover:text-white hover:border-slate-800 transition-colors">
                    <History className="w-3 h-3" /> Düzeltme Geçmişi
                  </button>
                )}
                {canEdit && !summary && <span className="text-[10px] text-slate-500">Satırların üzerine gelince çıkan <b>+</b> ile ilgili satırın altına, o bölge seçili olarak eklenir; düzeltme satırlarında kalem simgesiyle yerinde düzenlenir.</span>}
              </div>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

// Özet görünümü: alt hizmet bazlı toplanmış satırlar + genel toplam (Excel ÖZET sayfasıyla aynı)
function SummaryRows({ rows, testid }) {
  const total = rows.reduce((s, r) => s + (Number(r.total) || 0), 0);
  return (
    <>
      {rows.map((r) => <ItemRow key={r.name} it={r} name={r.name} testid={`${testid}-sum-${r.i}`} />)}
      {rows.length > 0 && (
        <tr className="border-t-2 border-slate-300 bg-slate-100" data-testid={`${testid}-summary-total`}>
          <td colSpan={4} className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-700 text-right">Toplam</td>
          <td className="numeric-cell px-3 py-1.5 text-xs font-extrabold text-slate-900">{fmtTL(total)}</td>
        </tr>
      )}
    </>
  );
}

function RegionBlock({ g, regional, testid, onAddBelow, onDelete, onEdit, adding, form, editing, editForm }) {
  const label = g.region || (regional ? "Bölgesiz Kalemler" : "");
  return (
    <>
      {label && (
        <tr className="border-t border-slate-200 bg-slate-100/80" data-testid={`${testid}-region-${g.region || "none"}`}>
          <td colSpan={5} className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-700">{label}</td>
        </tr>
      )}
      {g.rows.map((r) => [
        editing === r.i && editForm ? editForm(g, r) : <ItemRow key={r.i} it={r} name={regional && g.region ? r.name : r.description} testid={`${testid}-row-${r.i}`} onAddBelow={onAddBelow} onDelete={onDelete} onEdit={onEdit} />,
        adding?.afterIndex === r.i ? form(`corr-${r.i}`, g.region) : null,
      ])}
      {label && g.rows.length > 1 && (
        <tr className="border-t border-slate-200 bg-slate-50" data-testid={`${testid}-region-total-${g.region || "none"}`}>
          <td colSpan={4} className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 text-right">{label} Toplamı</td>
          <td className="numeric-cell px-3 py-1.5 text-xs font-bold text-slate-900">{fmtTL(g.total)}</td>
        </tr>
      )}
    </>
  );
}
